import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsightsIcon from "@mui/icons-material/Insights";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

const WelcomeScreen = ({ onStart, darkMode }) => {
  const theme = useTheme();
  const isDark = darkMode ?? theme.palette.mode === "dark";

  // Detect small screen (mobile)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Sample data
  const data = [
    { name: "Jan", value: 40 },
    { name: "Feb", value: 55 },
    { name: "Mar", value: 75 },
    { name: "Apr", value: 60 },
  ];
  const pieData = [
    { name: "A", value: 45 },
    { name: "B", value: 30 },
    { name: "C", value: 25 },
  ];
  const COLORS = ["#3f51b5", "#ff9800", "#4caf50"];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: isDark ? "#0f172a" : "#f9fafb",
        color: isDark ? "#e2e8f0" : "inherit",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        // py: { xs: 4, sm: 6 },
      }}
    >
      {/* Welcome Title */}
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant={isSmallScreen ? "h4" : "h4"}
            fontWeight="bold"
            sx={{
              color: isDark ? "#f1f5f9" : "#1e293b",
              textAlign: "center",
              mb: 1,
              mt: 10,
              letterSpacing: "-0.02em",
            }}
          >
            Welcome to AskSAM
          </Typography>
          <Typography
            sx={{
              color: isDark ? "#94a3b8" : "text.secondary",
              textAlign: "center",
              mx: "auto",
            }}
          >
            Your AI-powered data assistant for instant insights and
            visualizations
          </Typography>
        </motion.div>
      </Container>

      {/* Charts Dashboard */}
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              bgcolor: isDark ? "#1e293b" : "#ffffff",
              display: "flex",
              flexDirection: "column",
              width: "fit-content",
              height: "fit-content",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 1,
                textAlign: "center",
                letterSpacing: "0.05em",
              }}
            >
              Data Insights Preview
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1 },
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Line Chart */}
              <Paper
                elevation={2}
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 60, sm: 80 },
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "transparent",
                  boxShadow: "none",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={data}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={isDark ? "#3f86ff" : "#3f51b5"}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              {/* Bar Chart */}
              <Paper
                elevation={2}
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 60, sm: 80 },
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "transparent",
                  boxShadow: "none",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <Bar
                      dataKey="value"
                      fill={isDark ? "#ffa500" : "#ff9800"}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              {/* Pie Chart */}
              <Paper
                elevation={2}
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 60, sm: 80 },
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "transparent",
                  boxShadow: "none",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      outerRadius={20}
                      innerRadius={8}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            isDark
                              ? ["#3f86ff", "#ffaa33", "#4caf50"][index % 3]
                              : COLORS[index % COLORS.length]
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      {/* Description, Features, Button */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: isDark ? "#94a3b8" : "text.secondary",
              lineHeight: 1.7,
              maxWidth: { xs: "100%", sm: 520 },
              mx: "auto",
              textAlign: "center",
            }}
          >
            Upload your files, analyze data, and get instant insights. AskSam
            helps you transform raw data into meaningful stories.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 4, justifyContent: "center" }}>
            {[
              {
                icon: <UploadFileIcon fontSize="large" color="primary" />,
                text: "Upload CSV, Excel, or JSON files",
              },
              {
                icon: <InsightsIcon fontSize="large" color="secondary" />,
                text: "Auto detect schema & generate insights",
              },
              {
                icon: <AutoGraphIcon fontSize="large" color="success" />,
                text: "Visualize your data instantly",
              },
            ].map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  elevation={4}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: isDark ? "#1e293b" : "#ffffff",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    },
                    textAlign: "center",
                    flexDirection: isSmallScreen ? "column" : "row",
                  }}
                >
                  {f.icon}
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? "#cbd5e1" : "text.secondary",
                      flex: 1,
                    }}
                  >
                    {f.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: "bold",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                },
              }}
              onClick={onStart}
              fullWidth={isSmallScreen}
            >
              Get Started
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default WelcomeScreen;
